import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Copy, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { getOrderByOrderNumber } from '../services/orders';
import { formatCurrency } from '../lib/currency';
import { useToast } from '../hooks/use-toast';
import { getSiteSettings } from '../services/settings';
import PaymentLogos from '../components/payment/PaymentLogos';

const OrderConfirmationPage = () => {
  const { t } = useTranslation();
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const orderData = await getOrderByOrderNumber(orderNumber);
        setOrder(orderData);
        const settings = await getSiteSettings();
        const mappedAccounts = (settings?.bank_accounts || []).map((account) => ({
          bankName: account.bank || account.bankName || '-',
          accountName: account.name || account.accountName || '-',
          accountNumber: account.accountNumber || '-',
          IBAN: account.iban || account.IBAN || '-',
          branchCode: account.branchCode || '',
        }));
        setBankAccounts(mappedAccounts);
      } catch (error) {
        console.error('Error fetching order:', error);
        toast({
          title: t('errors.generic'),
          description: t('order.notFound'),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    if (orderNumber) {
      fetchOrder();
    }
  }, [orderNumber, t, toast]);

  const handleCopyIBAN = (iban) => {
    navigator.clipboard.writeText(iban);
    toast({
      title: t('common.copied'),
      description: 'IBAN kopyalandı',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <p>{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-2xl font-semibold mb-4">{t('order.notFound')}</h1>
          <Button onClick={() => navigate('/')}>{t('common.home')}</Button>
        </div>
      </Layout>
    );
  }

  const orderTotal = order.total || (order.subtotal + order.shipping + order.tax);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        <div className="text-center mb-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">{t('order.thankYou')}</h1>
          <p className="text-gray-600 mb-4">{t('order.orderPlaced')}</p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto flex items-center gap-3">
            <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
            <p className="text-green-800 text-sm">
              Sipariş onay maili <strong>{order?.customerInfo?.email || 'e-posta adresinize'}</strong> gönderildi.
            </p>
          </div>
        </div>

        {/* Order Number */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6 text-center">
          <p className="text-sm text-gray-600 mb-2">{t('order.orderNumber')}</p>
          <p className="text-2xl font-bold">{order.orderNumber}</p>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-lg p-6 border mb-6">
          <h2 className="text-xl font-semibold mb-4">{t('checkout.orderSummary')}</h2>
          
          <div className="space-y-3 mb-4">
            {order.items?.map((item, index) => (
              <div key={index} className="flex gap-3">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-16 h-20 object-cover rounded"
                />
                <div className="flex-1">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-sm text-gray-500">
                    {t('product.size')}: {item.size} | {t('product.color')}: {item.color}
                  </p>
                  <p className="text-sm">
                    {item.quantity} x {formatCurrency(item.price)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <Separator className="my-4" />

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('cart.subtotal')}</span>
              <span>{formatCurrency(order.subtotal || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('cart.shipping')}</span>
              <span>{formatCurrency(order.shipping || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('cart.tax')}</span>
              <span>{formatCurrency(order.tax || 0)}</span>
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between font-semibold text-lg">
              <span>{t('cart.total')}</span>
              <span>{formatCurrency(orderTotal)}</span>
            </div>
          </div>
        </div>

        {/* Bank Transfer Instructions */}
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200 mb-6">
          <h2 className="text-xl font-semibold mb-4">{t('payment.bankTransfer')}</h2>
          <p className="text-gray-700 mb-4">{t('payment.transferInstructions')}</p>
          <div className="bg-white border rounded-md p-3 mb-4">
            <p className="text-sm font-medium text-gray-800">Kart ödemeleri için altyapı sağlayıcıları</p>
            <PaymentLogos variant="confirmation" showIyzicoLabel />
            <p className="text-xs text-gray-600 mt-2">Bu sipariş için ödeme yöntemi: Havale/EFT</p>
          </div>

          <div className="space-y-4">
            {bankAccounts.length > 0 ? (
              bankAccounts.map((account, index) => (
                <div key={index} className="bg-white rounded p-4 border">
                  <h3 className="font-semibold mb-3">{account.bankName}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payment.accountHolder')}:</span>
                      <span className="font-medium">{account.accountName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payment.accountNumber')}:</span>
                      <span className="font-medium">{account.accountNumber}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('payment.iban')}:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium font-mono">{account.IBAN}</span>
                        <button
                          onClick={() => handleCopyIBAN(account.IBAN)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title={t('common.copy')}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                    {account.branchCode && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('payment.branchCode')}:</span>
                        <span className="font-medium">{account.branchCode}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded p-4 border text-sm text-gray-600">
                Banka hesap bilgileri henüz tanımlanmamış. Lütfen kısa süre sonra tekrar kontrol edin.
              </div>
            )}
          </div>

          <div className="mt-4 p-4 bg-yellow-50 rounded border border-yellow-200">
            <p className="text-sm font-medium text-yellow-800">
              {t('payment.reference')}: <span className="font-mono">{order.orderNumber}</span>
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              {t('payment.amount')}: <span className="font-bold">{formatCurrency(orderTotal)}</span>
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            onClick={() => navigate('/order-tracking')}
            className="flex items-center gap-2"
          >
            {t('order.trackOrder')}
            <ArrowRight size={18} />
          </Button>
          <Button
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            {t('common.home')}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default OrderConfirmationPage;
